import Button from 'components/Basic/Button';
import {
	EditFacetModalBtnWrap,
	FacetTitle, FacetTitleButtons,
	FacetWrapper, ModalContent, ModalFacetInfoWrap,
	ModalFacetWrap, StyledPopup, DivnoRoof
} from './style';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import { 
	DESIGN_MODES, 
	FACET_HIGHLIGHT_COLOR, 
	FACET_DEFAULT_FILL_COLOR_ROOF_TOOL, 
	KONVA_FACET_GROUP, 
	NORMAL_FACET_DEFAULT_PITCH, 
	FLAT_ROOF_DEFAULT_PITCH 
} from '../../constants';
import FacetDetailCard from './facetDetailCard';
import { useCallback, useMemo, useRef, useState } from 'react';
import {
	findKonvaShapeByID,
	KonvaGroupNames,
	resetAllTransformations
} from 'components/tool/utils';
import { useRefs } from 'contexts/RefContext';
import Konva from 'konva';
import { debounce } from '../../util';
import {
	setAzimuthOrPitchForNewlyCreatedNrelFacets,
	setDeletedRoofIndexes,
	setRoofIndexes,
	setSelectedRoofIndex,
	setUserModificationState
} from 'store/slices/ToolSlice';
import { NewlyCreatedFacet } from 'store/slices/ToolSlice/types';
import { toggleManualToolDoneButtonState, toggleManualToolModalState, toggleManualToolResetButtonState } from 'store/slices/ManualToolModal';
import useQueriesRunningInBackGround from 'hooks/useQueriesRunningInBackGround';
import useCreateDeleteFacet from 'hooks/api/facet/useCreateDeleteFacet';
import useEditPitch from 'hooks/api/facet/useEditPitch';
import { changePanel } from 'store/slices/RoofDataSlice/roofDataSlice';
import { toggleResetChangesLayoutModalSliceState } from 'store/slices/ResetChangesSlice';
import SwitchToggle from 'components/Basic/SwitchToggle/SwitchToggle';
import { selectIsRecomputing } from 'store/slices/DsmShadingSlice/selectors';
const highlightTimer = 5000;

export function FacetListModal() {

	const { konvaRef } = useRefs();
	const timeOut = useRef<NodeJS.Timeout>();
	const dispatch = useDispatch<AppDispatch>();

	const { open, showAllFacets, inputFieldsEdited } =
		useSelector((state: RootState) => state.manualToolModal.data);
	const { mode, allRoofSegs, uuid, designType } = useSelector((s: RootState) => s.roofData.data);
	const { selectedRoofIndex, roofIndexes, newlyCreatedFacets, drawModeEnabled,
		selectedUnfinalizedKonvaFacetId, deletedRoofIndexes
	} = useSelector((s: RootState) => s.toolNewPostions.data);
	const allowedActions = useSelector((state: RootState) => state.roleState.data.allowedActions);
	const canEditFacetProps = allowedActions.includes('translate-panels-to-new-position');
	const isRecomputing = useSelector(selectIsRecomputing);

	const [selectedGrpID, setSelectedGrp] = useState<string | null>(null);
	const [showPitchInDegree, setShowPitchInDegree] = useState<boolean>(false);

	const isRoofDefaultMode = [
		DESIGN_MODES.DEFAULT,
		DESIGN_MODES.NEARMAP_NREL_AUTOMATIC,
		DESIGN_MODES.DEFAULT_MANUAL,
		DESIGN_MODES.IN_HOUSE_SIMULATION,
		DESIGN_MODES.IN_HOUSE_SIMULATION_MANUAL
	].includes(mode as DESIGN_MODES);
	const hasTransformedFacet = !!roofIndexes.length;
	const hasCreatedFacet = !!Object.keys(newlyCreatedFacets).length;
	const hasDeletedFacet = !!deletedRoofIndexes.length;
	const hasRoofModifications = hasTransformedFacet || hasCreatedFacet || hasDeletedFacet;

	const roofUpdateQueryRunning =
		useQueriesRunningInBackGround(['create-new-facets', 'update-roof-pitch'], 'mutating');
	const createNewFacetsQuery = useCreateDeleteFacet();
	const updateRoofPitchQuery = useEditPitch();

	// TODO move this to common util genric fn
	const highlightGroup = useCallback((id: string, groupSuffix: string) => {
		if (!konvaRef?.current) return;

		if (selectedGrpID === id) return;
		// if user clicks on diff group then prev group and the pre grp is still highlighted
		if (selectedGrpID && selectedGrpID !== id) {
			clearTimeout(timeOut.current);
			const group: Konva.Group | undefined = findKonvaShapeByID(konvaRef?.current, `${selectedGrpID}${groupSuffix}`) as Konva.Group;
			const facet = group?.findOne(`.${KonvaGroupNames.existingFacetsGroup}${KONVA_FACET_GROUP.GROUP_NAME_SEPARATOR}facet`) as Konva.Line;
			if (facet) facet.fill(FACET_DEFAULT_FILL_COLOR_ROOF_TOOL);
		}
		setSelectedGrp(id);

		const group: Konva.Group = findKonvaShapeByID(konvaRef?.current, `${id}${groupSuffix}`) as Konva.Group;
		const facet = group?.findOne(`.${KonvaGroupNames.existingFacetsGroup}${KONVA_FACET_GROUP.GROUP_NAME_SEPARATOR}facet`) as Konva.Line;
		console.log(facet, 'facet');
		if (facet) facet.fill(FACET_HIGHLIGHT_COLOR);

		clearTimeout(timeOut.current);
		timeOut.current = setTimeout(() => {
			if (facet) facet.fill(FACET_DEFAULT_FILL_COLOR_ROOF_TOOL);
			// setSelectedGrp(null);
		}, highlightTimer);
	}, [konvaRef, selectedGrpID]);

	const debouncePitchForSystemDefinedFacets = debounce((id: string, groupSuffix: string, value: number) => {
		const group: Konva.Group | undefined = findKonvaShapeByID(konvaRef?.current, `${id}${groupSuffix}`) as Konva.Group;
		group?.setAttr('pitch', value);
		if (!roofIndexes.includes(id))
			dispatch(setRoofIndexes({ roofIndexes: roofIndexes.concat(id) }));
	}, 200);

	const updatePitchForExistingFacets = useCallback((id: string, groupSuffix: string, value: number) => {
		if (groupSuffix.slice(1) != KONVA_FACET_GROUP.IN_PROGRESS_GROUP_NAME) {
			dispatch(toggleManualToolDoneButtonState({ inputFieldsEdited: true }));
			dispatch(toggleManualToolResetButtonState({ resetEnabled: true }));
		}

		debouncePitchForSystemDefinedFacets(id, groupSuffix, value);
	}, [debouncePitchForSystemDefinedFacets, dispatch]);

	const getExistingFacetConfg = useCallback((existingFacet: RasterRoofSegment): React.ComponentProps<typeof FacetDetailCard> => {
		const groupSuffix = `${KONVA_FACET_GROUP.GROUP_NAME_SEPARATOR}${KONVA_FACET_GROUP.EXISTING_GROUP_NAME}`;
		const group = findKonvaShapeByID(konvaRef?.current, `${existingFacet.id}${groupSuffix}`);
		return ({
			facet: {
				area: existingFacet.area ?? null,
				pitchDeg: group?.getAttr('pitch') ?? existingFacet.pitchDegrees ?? null,
				prevPitchDeg: group?.getAttr('pitch') ? existingFacet.pitchDegrees : undefined,
				azimuthDeg: existingFacet.azimuthDegrees ?? null,
				hullcoords: existingFacet.simplifiedHullCoords,
				konvaPolygonId: existingFacet.id,
				label: existingFacet.label,
				// name: getExistingFacetDisplayName(konvaRef?.current, existingFacet),
				// edges: existingFacet.edges,
				groupSuffix: `${KONVA_FACET_GROUP.GROUP_NAME_SEPARATOR}${KONVA_FACET_GROUP.EXISTING_GROUP_NAME}`
			},
			onPitchValChange: updatePitchForExistingFacets,
			onCardClick: highlightGroup,
			active: existingFacet.id === selectedRoofIndex,
			enablePitchEditing: canEditFacetProps && !isRecomputing,
			showPitchInFraction: !showPitchInDegree,
			isEdited: roofIndexes.includes(existingFacet.id),
		});

	}, [canEditFacetProps, isRecomputing, highlightGroup, konvaRef, roofIndexes, selectedRoofIndex, showPitchInDegree, updatePitchForExistingFacets]);

	// InProgress Facets
	const debouncePitchDispatchForUserDefinedFacets = debounce((id: string, groupSuffix: string, value: number,) => {
		dispatch(setAzimuthOrPitchForNewlyCreatedNrelFacets({
			facetId: id,
			pitchDegrees: value,
		}));
	}, 200);

	const getInProgressFacetConfg = useCallback((newlyCreatedFacet: NewlyCreatedFacet): React.ComponentProps<typeof FacetDetailCard> => {
		return ({
			facet: {
				area: null,
				pitchDeg: newlyCreatedFacet.pitchDegrees ?? (newlyCreatedFacet.isGroundMount ? FLAT_ROOF_DEFAULT_PITCH : NORMAL_FACET_DEFAULT_PITCH),
				azimuthDeg: newlyCreatedFacet.azimuthDegrees ?? null,
				hullcoords: newlyCreatedFacet.hullCoords,
				konvaPolygonId: newlyCreatedFacet.konvaPolygonId,
				groupSuffix: `${KONVA_FACET_GROUP.GROUP_NAME_SEPARATOR}${KonvaGroupNames.inProgressUserDefinedFacetGroup}`,
				label: undefined,
			},
			onPitchValChange: debouncePitchDispatchForUserDefinedFacets,
			onCardClick: highlightGroup,
			active: newlyCreatedFacet.konvaPolygonId === selectedUnfinalizedKonvaFacetId,
			enablePitchEditing: (!isRoofDefaultMode || !!newlyCreatedFacet.isGroundMount) && !isRecomputing,
			showPitchInFraction: !showPitchInDegree,
		});

	}, [debouncePitchDispatchForUserDefinedFacets, highlightGroup, isRoofDefaultMode, isRecomputing, selectedUnfinalizedKonvaFacetId, showPitchInDegree]);

	const roofSegmentsList = useMemo(() => {
		let facetsConfig: React.ComponentProps<typeof FacetDetailCard>[] = [];

		const shouldShowAll = showAllFacets || (!drawModeEnabled && !selectedRoofIndex);
		if (shouldShowAll) {
			facetsConfig = Object.values(newlyCreatedFacets).map(getInProgressFacetConfg);
			facetsConfig = facetsConfig.concat(allRoofSegs.map(getExistingFacetConfg));
		}
		else if (drawModeEnabled) {
			facetsConfig = Object.values(newlyCreatedFacets).map(facet => getInProgressFacetConfg(facet));
		}
		else if (selectedRoofIndex) {
			const selectedFacet = allRoofSegs.find((r) => r.id === selectedRoofIndex);
			facetsConfig = selectedFacet ? [getExistingFacetConfg(selectedFacet)] : [];
		}
		return facetsConfig;
	}, [allRoofSegs, drawModeEnabled, getExistingFacetConfg, getInProgressFacetConfg, newlyCreatedFacets, selectedRoofIndex, showAllFacets]);

	const resetToLoadedState = () => {
		roofSegmentsList.forEach(({ facet }) => {
			if ('prevPitchDeg' in facet && facet.prevPitchDeg !== undefined) {
				const grp = findKonvaShapeByID(konvaRef?.current, `${facet.konvaPolygonId}${facet.groupSuffix}`) as Konva.Group;
				if (grp) grp.setAttr('pitch', facet.prevPitchDeg);
				facet.prevPitchDeg = undefined;
			}
		});
		if (konvaRef?.current) resetAllTransformations(konvaRef.current, true);
		dispatch(toggleManualToolDoneButtonState({ inputFieldsEdited: false }));
		dispatch(toggleManualToolResetButtonState({ resetEnabled: false }));
		dispatch(setRoofIndexes({ roofIndexes: [] }));
		dispatch(setSelectedRoofIndex({ selectedRoofIndex: null }));

	};

	const handleDoneBtnClick = debounce(async () => {
		try {
			// facet create/delete must resolve first: a pending delete can invalidate facet ids
			// the pitch-update mutation below references, so it must not fire while it's in flight.
			await Promise.allSettled([createNewFacetsQuery.mutateAsync()]);
			await Promise.allSettled([updateRoofPitchQuery.mutateAsync()]);
			await handleQueiresSuccess();
			dispatch(toggleManualToolModalState({ value: false }));
			dispatch(toggleManualToolDoneButtonState({ inputFieldsEdited: false }));
			dispatch(toggleManualToolResetButtonState({ resetEnabled: false }));
		} catch (error) {
			console.error('Error while executing queries:', error);
		}
	}, 250);

	const handleQueiresSuccess = async () => {
		await dispatch(changePanel({ uuid, designType: designType }));
		dispatch(setRoofIndexes({ roofIndexes: [] }));
		dispatch(setDeletedRoofIndexes({ deletedRoofIndexes: [] }));
		dispatch(setUserModificationState({ hasMadeChanges: true }));
	};

	function handlePopupClose() {
		if (inputFieldsEdited) {
			dispatch(toggleResetChangesLayoutModalSliceState(
				{ 
					open: true, 
					warningMessage: 'Do you want to discard the changes and exit this menu?',
					allFacetsList: roofSegmentsList.map((f) => ({
						previousPitch: f.facet.prevPitchDeg,
						azimuthDegrees: f.facet.azimuthDeg,
						konvaPolygonId: f.facet.konvaPolygonId,
						groupSuffix: f.facet.groupSuffix,
					})),
				}
			));
			dispatch(toggleManualToolModalState({ value: false }));
		} else {
			dispatch(toggleManualToolModalState({ value: false }));
		}
	}

	return (
		<StyledPopup
			open={open}
			closeOnDocumentClick={true}
			onClose={handlePopupClose}
			className={open ? 'open' : 'close'}
		>
			<ModalContent className="modal-content">
				<ModalFacetWrap>
					<FacetWrapper>
						<FacetTitle>Facet Details:</FacetTitle>
						<FacetTitleButtons>
							{(inputFieldsEdited) && (
								<Button
									onClick={async () => {
										resetToLoadedState();
									}}
									className='btn-reset'>
									<svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
										<path d="M8.5 10.4853H11.1069M13.1123 8.94116V10.4853M13.1123 12.0294V10.4853M13.1123 
											10.4853H14.6764" stroke="var(--defaultIconColor)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
										<path d="M14.6758 14.456H12.0688M10.0635 12.9119V14.456M10.0635 16.0001V14.456M10.0635 
											14.456H8.49933" stroke="var(--defaultIconColor)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
										<path d="M5.7317 13.5996C3.01246 12.8991 1 10.3877 1 7.39704C1 3.86405 3.80845 1 7.27284 1C10.2025 
										1 12.6631 3.0482 13.3529 5.81707M5.7317 13.5996V10.8863M5.7317 13.5996V13.7941H2.71077"
										stroke="var(--defaultIconColor)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
									</svg>
										Reset
								</Button>
							)}
							{(inputFieldsEdited || hasRoofModifications) && (
								<Button
									showLoader={roofUpdateQueryRunning}
									disabled={roofUpdateQueryRunning || isRecomputing}
									onClick={handleDoneBtnClick}
								>
										Done
								</Button>
							)}
						</FacetTitleButtons>
					</FacetWrapper>
					<div className='switchButton pitch-toggle'>
						<span>Show pitch in degrees</span>
						<SwitchToggle
							checked={showPitchInDegree}
							onChange={() => setShowPitchInDegree((prevVAl) => !prevVAl)}
							disabled={false}
						/>
					</div>
					<ModalFacetInfoWrap className="modal-facet-info-wrap a">
						{!roofSegmentsList.length && <DivnoRoof>No Roof Segments.	</DivnoRoof>}
						{roofSegmentsList.map((config) => (
							<FacetDetailCard
								key={config.facet.konvaPolygonId}
								{...config}
								active={(selectedGrpID === config.facet.konvaPolygonId)}
								roofUpdateQueryRunning={roofUpdateQueryRunning || isRecomputing}
							/>
						))}
					</ModalFacetInfoWrap>
				</ModalFacetWrap>
			</ModalContent>

		</StyledPopup>

	);
}	